import { useGetClientPropertiesQuery } from '@/features/clients/clientsApi';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useGetRelationshipCompanyQuery } from '@/features/relationshipCompanies/relationshipCompaniesApi';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import getCorrectFolderOwner from '@/hooks/getCorrectFolderOwner';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { safelyParseDate } from '@/utils/date';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const FolderCreationContext = createContext({});

export const useFolderCreationContext = () => {
	const context = useContext(FolderCreationContext);
	if (!context) {
		throw new Error(
			'useFolderCreationContext must be used within an FolderCreationContextProvider',
		);
	}
	return context;
};

export const FolderCreationContextProvider = ({ children }) => {
	const params = useParams();
	const [loadingClient, setLoadingClient] = useState();
	const [loadingDeal, setLoadingDeal] = useState();
	const [loadingCompany, setLoadingCompany] = useState();

	const { currentData: dealRow } = useGetDealRowQuery(params?.dealId, {
		skip: !params?.dealId,
	});

	const { currentData: relationshipCompany } = useGetRelationshipCompanyQuery(
		params?.relationshipCompanyId,
		{ skip: !params?.relationshipCompanyId },
	);

	const clientId = params?.clientId ?? getDealClientId(dealRow);

	const { currentData: clientProps } = useGetClientPropertiesQuery(clientId, {
		skip: !clientId,
	});
	const clientRow = clientProps?.clientRow;

	const { correctOwner: folderOwnerId, loading: loadingOwner } = getCorrectFolderOwner({
		deal: dealRow,
		client: clientRow,
		relationshipCompany,
	});

	const { data: folderOwner, ...folderOwnerQuery } = useGetUserDetailsQuery(
		{ userId: folderOwnerId },
		{ skip: !folderOwnerId },
	);

	const isValidSharepoint =
		folderOwner?.sharepointSite != null &&
		folderOwner?.clientSiteSuffix != null &&
		folderOwner?.clientFolder != null &&
		folderOwner?.clientSite != null;

	const {
		createClientFolder,
		updateClientGraphProperties,
		getClientDriveId,
		getRelationshipCompanyDriveId,
	} = useClientStorageHooks(
		dealRow?.brokerGroupId ?? clientRow?.brokerGroupId ?? relationshipCompany?.brokerGroupId,
	);

	const createSharepoint = async () => {
		try {
			const date = dealRow?.createDate ? safelyParseDate(dealRow?.createDate) : new Date();

			await createClientFolder(params?.dealId, {
				deal: dealRow,
				client: clientRow,
				objectType: dealRow?.objectType,
				...(dealRow?.yearRoot ? { yearRoot: dealRow?.yearRoot.split('/')[1] } : { date }),
			});
		} catch (error) {
			console.log(`Error creating deal folder for deal ${dealRow?.dealName}`, error);
		}
	};

	useEffect(() => {
		const updateClientMsGraph = async () => {
			setLoadingClient(true);
			if (clientRow?.driveId && clientRow?.clientFolderDriveId) {
				await getClientDriveId(params?.clientId ?? clientRow?.clientId);
			} else {
				await updateClientGraphProperties({ clientRow });
			}
			setLoadingClient(false);
		};
		if (isValidSharepoint && clientRow && clientRow?.canViewClient) {
			updateClientMsGraph();
		}
	}, [clientRow, isValidSharepoint]);

	useEffect(() => {
		const updateDealMsGraph = async () => {
			setLoadingDeal(true);
			const res = await updateClientGraphProperties({ clientRow, dealRow });
			if (
				!res?.driveId &&
				isValidSharepoint &&
				folderOwner?.sharepointSiteId &&
				folderOwner?.clientFolderDriveId
			) {
				await createSharepoint();
			}
			setLoadingDeal(false);
		};
		if (
			isValidSharepoint &&
			dealRow?.canViewDeal &&
			clientRow &&
			dealRow &&
			(!dealRow?.driveId || !dealRow?.clientFolderDriveId)
		) {
			updateDealMsGraph();
		}
	}, [dealRow, isValidSharepoint]);

	useEffect(() => {
		const updateCompanyMsGraph = async () => {
			setLoadingCompany(true);
			if (relationshipCompany?.driveId && relationshipCompany?.clientFolderDriveId) {
				await getRelationshipCompanyDriveId(relationshipCompany.id);
			} else {
				await updateClientGraphProperties({ relationshipCompany });
			}
			setLoadingCompany(false);
		};
		if (isValidSharepoint && relationshipCompany) {
			updateCompanyMsGraph();
		}
	}, [relationshipCompany, isValidSharepoint]);

	const context = {
		...clientProps,
		clientRow,
		dealRow,
		relationshipCompany,
		...(params ?? {}),
		loading:
			loadingClient ||
			loadingDeal ||
			loadingCompany ||
			loadingOwner ||
			folderOwnerQuery.isLoading,
		folderOwnerId,
	};

	return (
		<FolderCreationContext.Provider value={context}>{children}</FolderCreationContext.Provider>
	);
};
